import { useMemo } from 'react';

import { Button } from '#/components/Button/Button';
import { Carousel } from '#/components/Carousel/Carousel';
import { useCarouselState } from '#/components/Carousel/useCarouselState';
import { StyledVersionedRouterLink } from '#/components/Link/StyledVersionedRouterLink';
import { useIsClient } from '#/hooks/useIsClient';
import type { SearchAlgoliaJobHit } from '#/modules/search/algolia/types/SearchAlgoliaJobHit';
import type { SearchAlgoliaRecommendedListingHit } from '#/modules/search/algolia/types/SearchAlgoliaRecommendedListingHit';
import { range } from '#/utils/functional/array/range';

import { SearchHitCarouselHolder } from './SearchHitCarousel.styled';
import { SearchHitCarouselHeader } from './SearchHitCarouselHeader';
import { SearchHitCarouselNavigation } from './SearchHitCarouselNavigation';
import { SearchHitCarouselTile } from './SearchHitCarouselTile';

type Props = {
  headline: string | null | undefined;
  results?: SearchAlgoliaJobHit[] | SearchAlgoliaRecommendedListingHit[];
  seeAll: {
    text: string;
    action:
      | { type: 'link'; url: string; state?: Record<string, unknown> }
      | { type: 'button'; onClick: () => void };
  };
  listingType: string;
  carouselLocation?: string;
  fromListingId?: string;
  layout?: 'SLIM' | 'REGULAR';
  source?: string;
  additionalAnalyticsProps?: Record<string, string>;
};

export function SearchHitCarousel({
  headline,
  results,
  seeAll,
  listingType,
  carouselLocation,
  fromListingId,
  layout = 'REGULAR',
  source,
  additionalAnalyticsProps,
}: Props) {
  const carouselState = useCarouselState({
    slideCount: results?.length ?? 0,
    responsiveSlidesPerPage:
      layout === 'SLIM'
        ? { xs: 1, sm: 1, md: 2, lg: 3 }
        : { xs: 1, sm: 1, md: 3, lg: 4 },
  });

  const carouselHeight = layout === 'SLIM' ? 205 : 338;

  const linkNode = useMemo(() => {
    if (!seeAll) return null;

    switch (seeAll.action.type) {
      case 'link':
        return (
          <StyledVersionedRouterLink
            qaId="search-hit-carousel-see-all"
            to={seeAll.action.url}
            state={seeAll.action.state}
          >
            {seeAll.text}
          </StyledVersionedRouterLink>
        );
      case 'button':
        return (
          <Button
            type="button"
            variant="tertiary"
            qaId="search-hit-carousel-see-all"
            onClick={seeAll.action.onClick}
          >
            {seeAll.text}
          </Button>
        );
    }
  }, [seeAll]);

  const isClient = useIsClient();
  return (
    <>
      <SearchHitCarouselHeader
        layout={layout}
        headline={headline}
        linkNode={linkNode}
      />

      <SearchHitCarouselHolder $layout={layout}>
        <Carousel
          state={carouselState}
          height={carouselHeight}
          qaId="search-hit-carousel"
          navigation={
            isClient && (
              <SearchHitCarouselNavigation carouselState={carouselState} />
            )
          }
          slides={
            results && results.length > 0
              ? results.map((result) => (
                  <div
                    key={result.objectID}
                    data-qa-id="search-hit-carousel-slide"
                    data-qa-listing-id={result.objectID}
                  >
                    <SearchHitCarouselTile
                      result={result}
                      listingType={listingType}
                      carouselLocation={carouselLocation}
                      fromListingId={fromListingId}
                      layout={layout}
                      source={source}
                      additionalAnalyticsProps={additionalAnalyticsProps}
                    />
                  </div>
                ))
              : range(4).map((index) => (
                  <div key={`section-${index}`}>
                    <div />
                  </div>
                ))
          }
        />
      </SearchHitCarouselHolder>
    </>
  );
}
